import { moonPosition } from '@trajectory/astro';
import type { TelemetrySeries } from '@/lib/telemetry-series';
import { findIndexForTime } from '@/lib/telemetry-series';
import { eciToThree } from './constants';

export type ScenePoint = [number, number, number];

/**
 * Convert telemetry into one continuous geocentric inertial display frame.
 *
 * Earth ascent and trans-lunar samples are already geocentric. Once the
 * propagator enters the lunar sphere of influence it intentionally switches to
 * Moon-centred coordinates, so the Moon ephemeris must be added back before a
 * continuous Earth–Moon trajectory can be drawn.
 */
export function geocentricTelemetryPosition(
  series: TelemetrySeries,
  index: number,
): ScenePoint | null {
  if (index < 0 || index >= series.t.length) return null;

  const position: ScenePoint = [
    series.positionX[index]!,
    series.positionY[index]!,
    series.positionZ[index]!,
  ];
  const frameCode = series.referenceFrameCode[index];
  const bodyCode = series.centralBodyCode[index];

  if (frameCode === 0 || frameCode === 1) return position;
  if (frameCode === 3 && bodyCode === 1) {
    const moon = moonPosition(series.epoch[index]!);
    return [moon[0] + position[0], moon[1] + position[1], moon[2] + position[2]];
  }

  // Heliocentric and non-lunar body-centred legs need a solar-system scale
  // rather than the Earth-scale scene used by this viewport.
  return null;
}

export function sceneTelemetryPosition(series: TelemetrySeries, index: number): ScenePoint | null {
  const point = geocentricTelemetryPosition(series, index);
  return point ? eciToThree(point) : null;
}

/** Smooth visual interpolation across sparse long-coast telemetry samples. */
export function interpolateSceneTelemetryPosition(
  series: TelemetrySeries,
  timeSeconds: number,
): { index: number; fraction: number; point: ScenePoint } | null {
  const index = findIndexForTime(series.t, timeSeconds);
  if (index < 0) return null;

  const current = sceneTelemetryPosition(series, index);
  if (!current) return null;
  const nextIndex = Math.min(index + 1, series.t.length - 1);
  const next = sceneTelemetryPosition(series, nextIndex);
  const interval = series.t[nextIndex]! - series.t[index]!;
  if (!next || nextIndex === index || interval <= 0) {
    return { index, fraction: 0, point: current };
  }

  const fraction = Math.min(1, Math.max(0, (timeSeconds - series.t[index]!) / interval));
  return {
    index,
    fraction,
    point: [
      current[0] + (next[0] - current[0]) * fraction,
      current[1] + (next[1] - current[1]) * fraction,
      current[2] + (next[2] - current[2]) * fraction,
    ],
  };
}

export function geocentricSceneBounds(series: TelemetrySeries): {
  center: ScenePoint;
  radius: number;
} | null {
  let minX = Number.POSITIVE_INFINITY;
  let minY = Number.POSITIVE_INFINITY;
  let minZ = Number.POSITIVE_INFINITY;
  let maxX = Number.NEGATIVE_INFINITY;
  let maxY = Number.NEGATIVE_INFINITY;
  let maxZ = Number.NEGATIVE_INFINITY;
  let found = false;

  for (let index = 0; index < series.t.length; index += 1) {
    const point = sceneTelemetryPosition(series, index);
    if (!point) continue;
    found = true;
    minX = Math.min(minX, point[0]);
    minY = Math.min(minY, point[1]);
    minZ = Math.min(minZ, point[2]);
    maxX = Math.max(maxX, point[0]);
    maxY = Math.max(maxY, point[1]);
    maxZ = Math.max(maxZ, point[2]);
  }
  if (!found) return null;

  const center: ScenePoint = [(minX + maxX) / 2, (minY + maxY) / 2, (minZ + maxZ) / 2];
  let radius = 0;
  for (let index = 0; index < series.t.length; index += 1) {
    const point = sceneTelemetryPosition(series, index);
    if (!point) continue;
    radius = Math.max(
      radius,
      Math.hypot(point[0] - center[0], point[1] - center[1], point[2] - center[2]),
    );
  }
  return { center, radius };
}
